import { Link } from 'react-router-dom';
import { mt5Steps } from '@/mocks/tradingData';
import { tradingSummary } from '@/utils/tradingSummary';
import Reveal from '@/components/base/Reveal';

// The two XAUUSD result cells are read off the shared ledger (via tradingSummary)
// so they can never disagree with the trade record. The surrounding prices stay
// clearly-labelled sample figures.
const terminalRows = [
  { symbol: 'XAUUSD', bid: '4462.80', ask: '4463.04', change: '+0.38%' },
  { symbol: 'XAUUSD · SELL', bid: '4445.31', ask: '4445.55', change: tradingSummary.bestShortTrade },
  { symbol: 'XAUUSD · BUY', bid: '4484.54', ask: '4484.78', change: tradingSummary.bestLongTrade },
  { symbol: 'EURUSD', bid: '1.0842', ask: '1.0844', change: '—' },
];

function MT5Section() {
  return (
    <section
      id="mt5"
      className="w-full bg-background-100 border-y border-background-200 px-4 md:px-8 lg:px-12 py-16 md:py-24"
    >
      <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* mock MT5 window */}
        <Reveal>
          <div className="rounded-lg border border-background-300 bg-background-50 overflow-hidden">
            <div className="flex items-center gap-2 px-4 h-11 border-b border-background-200 bg-background-100">
              <span className="w-2.5 h-2.5 rounded-full bg-background-400"></span>
              <span className="w-2.5 h-2.5 rounded-full bg-background-400"></span>
              <span className="w-2.5 h-2.5 rounded-full bg-background-400"></span>
              <span className="ml-3 text-[11px] font-data tracking-wider text-foreground-500">
                MetaTrader 5 · Market Watch
              </span>
              <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-background-200 px-2 py-0.5 text-[9px] font-data tracking-wider text-foreground-500">
                <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                SAMPLE DATA
              </span>
            </div>
            <div className="px-4 py-4">
              <div className="grid grid-cols-[1.4fr_1fr_1fr_0.9fr] gap-2 pb-2 border-b border-background-200 text-[10px] font-data tracking-wider text-foreground-400">
                <span>SYMBOL</span>
                <span className="text-right">BID</span>
                <span className="text-right">ASK</span>
                <span className="text-right">RESULT</span>
              </div>
              <div className="divide-y divide-background-200">
                {terminalRows.map((row) => (
                  <div
                    key={row.symbol}
                    className="grid grid-cols-[1.4fr_1fr_1fr_0.9fr] gap-2 py-2.5 items-center hover:bg-background-100 transition-colors cursor-default"
                  >
                    <span className="text-[12px] font-data text-foreground-900 truncate">
                      {row.symbol}
                    </span>
                    <span className="text-[12px] font-data text-foreground-600 text-right">
                      {row.bid}
                    </span>
                    <span className="text-[12px] font-data text-foreground-600 text-right">
                      {row.ask}
                    </span>
                    <span
                      className={`text-[12px] font-data text-right ${
                        row.change.startsWith('+') && row.change.includes('.')
                          ? 'text-profit-600'
                          : row.change === '—'
                            ? 'text-foreground-400'
                            : 'text-foreground-600'
                      }`}
                    >
                      {row.change}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="px-4 py-3 border-t border-background-200 bg-background-100 flex items-center justify-between">
              <span className="text-[10px] font-data tracking-wider text-foreground-500">
                AUTOTRADING
              </span>
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-profit-500"></span>
                <span className="text-[10px] font-data tracking-wider text-profit-600">
                  ENABLED
                </span>
              </div>
            </div>
            <p className="px-4 py-2.5 border-t border-background-200 text-[10px] font-data tracking-wider text-foreground-400">
              Illustrative sample figures — not live market quotes.
            </p>
          </div>
        </Reveal>

        {/* copy + steps */}
        <div>
          <Reveal delay={100}>
            <h2 className="font-heading font-extrabold tracking-tight text-foreground-950 text-[34px] leading-[1.08] sm:text-[42px] lg:text-[48px]">
              Built To Run On MT5
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-foreground-600 max-w-[520px]">
              Designed to operate through MetaTrader 5, giving users a familiar environment for
              setup, execution and monitoring.
            </p>
          </Reveal>

          <div className="mt-10 space-y-6">
            {mt5Steps.map((step, idx) => (
              <Reveal key={step.index} delay={180 + idx * 110}>
                <div className="flex items-start gap-5">
                  <span className="font-data text-[13px] tracking-wider text-primary-600 pt-1 shrink-0">
                    {step.index}
                  </span>
                  <div>
                    <h3 className="font-heading font-bold tracking-tight text-foreground-950 text-[18px]">
                      {step.title}
                    </h3>
                    <p className="mt-1.5 text-[13px] leading-relaxed text-foreground-500">
                      {step.detail}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={520}>
            <Link
              to="/mt5-setup"
              className="mt-10 inline-flex items-center gap-2 h-11 px-5 rounded-md border border-foreground-300 text-foreground-950 text-[14px] font-semibold whitespace-nowrap hover:border-primary-400 hover:text-primary-700 transition-all duration-200 cursor-pointer hover:-translate-y-0.5"
            >
              View MT5 Setup
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </span>
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default MT5Section;